class NwpSearch extends HTMLElement {
  connectedCallback() {
    this.cancel();
    this.listeners?.abort();
    this.listeners = new AbortController();
    this.cache ??= new Map();
    this.composing = false;
    this.form = this.querySelector('form');
    this.input = this.form?.elements.q;
    this.results = this.querySelector('[data-search-results]');
    this.idle = this.querySelector('[data-search-idle]');
    this.status = this.querySelector('[data-search-status]');
    this.dialog = this.closest('header-search');
    if (!this.input || !this.results || !this.dialog) return;
    const options = { signal: this.listeners.signal };
    this.input.addEventListener('input', () => this.schedule(), options);
    this.input.addEventListener('compositionstart', () => {
      this.composing = true;
      this.cancel();
      this.setState('pending');
    }, options);
    this.input.addEventListener('compositionend', () => {
      this.composing = false;
      this.schedule();
    }, options);
    this.form.addEventListener('submit', event => {
      if (this.composing || !this.input.value.trim()) {
        event.preventDefault();
        this.input.focus({ preventScroll: true });
      }
      else this.input.value = this.input.value.trim();
    }, options);
    this.form.addEventListener('reset', event => {
      event.preventDefault();
      this.input.value = '';
      this.cancel();
      this.results.replaceChildren();
      this.setState('idle');
      this.status.textContent = '';
      this.input.focus({ preventScroll: true });
    }, options);
    this.addEventListener('keydown', event => this.navigate(event), options);
    this.addEventListener('click', event => {
      const starter = event.target.closest('[data-search-query]');
      if (starter && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
        event.preventDefault();
        this.input.value = starter.dataset.searchQuery;
        this.input.focus({ preventScroll: true });
        this.schedule(true);
      } else if (event.target.closest('[data-search-retry]')) {
        this.input.focus({ preventScroll: true });
        this.schedule(true);
      }
    }, options);
    this.dialog.addEventListener('dialog:before-hide', () => {
      this.cancel();
      if (['pending', 'loading'].includes(this.dataset.state)) {
        this.restoreRetainedState();
      }
      this.status.textContent = '';
    }, options);
    this.dialog.addEventListener('dialog:before-show', () => this.resize(), options);
    // Prestige moves the modal out of the transformed header on opening. Its
    // reconnect cancels pending work, so resume retained queries after entry.
    this.dialog.addEventListener('dialog:after-show', () => {
      if (!this.dialog.hasAttribute('open')) return;
      if (this.input.value.trim()) this.schedule(true);
      this.resize();
    }, options);
    window.visualViewport?.addEventListener('resize', () => this.resize(), options);
    window.visualViewport?.addEventListener('scroll', () => this.resize(), options);
    window.addEventListener('resize', () => this.resize(), options);
    this.restoreRetainedState();
  }

  restoreRetainedState() {
    const query = this.input.value.trim();
    this.setState(query && this.results.hasChildNodes()
      ? this.resultsQuery === query ? 'results' : 'pending'
      : 'idle');
  }

  disconnectedCallback() {
    this.cancel();
    this.listeners?.abort();
  }

  cancel() {
    this.sequence = (this.sequence ?? 0) + 1;
    clearTimeout(this.debounceTimer);
    clearTimeout(this.loadingTimer);
    clearTimeout(this.timeoutTimer);
    this.request?.abort();
    this.request = null;
    this.results?.setAttribute('aria-busy', 'false');
  }

  resize() {
    if (!this.dialog?.hasAttribute('open')) return;
    const viewport = window.visualViewport;
    const height = viewport?.height ?? window.innerHeight;
    const gap = height < 320 ? 8 : height < 480 ? 32 : Math.max(76, Math.min(144, height * .16));
    const available = Math.max(0, height - gap - 16);
    this.dialog.style.setProperty('--nwp-search-viewport-top', (viewport?.offsetTop ?? 0) + 'px');
    this.dialog.style.setProperty('--nwp-search-top-gap', gap + 'px');
    this.dialog.style.setProperty('--nwp-search-available-height', available + 'px');
    // A short visual viewport, including a phone keyboard, needs one scroll
    // surface so the form and footer can both remain reachable.
    this.toggleAttribute('data-compact', available < 360);
  }

  setState(state) {
    this.dataset.state = state;
    const busy = state === 'pending' || state === 'loading';
    const retainResults = busy && this.results.hasChildNodes();
    this.idle.hidden = state !== 'idle' && !(state === 'pending' && !retainResults);
    this.idle.inert = busy;
    this.querySelector('[data-search-loading]').hidden = state !== 'loading' || retainResults;
    this.querySelector('[data-search-error]').hidden = state !== 'error';
    this.results.hidden = state !== 'results' && !retainResults;
    this.results.inert = busy;
    this.results.setAttribute('aria-busy', String(busy));
    const hasQuery = Boolean(this.input.value.trim());
    this.querySelector('[data-search-all]').hidden = !hasQuery;
    this.querySelector('[data-search-idle-note]').hidden = hasQuery;
    this.querySelector('[type="reset"]').hidden = !this.input.value;
    if (state === 'pending') this.status.textContent = '';
    if (state === 'loading') this.status.textContent = this.dataset.loadingMessage;
    if (state === 'error') this.status.textContent = [...this.querySelectorAll('[data-search-error] h2, [data-search-error] p')].map(node => node.textContent.trim()).filter(Boolean).join(' ');
  }

  schedule(immediate = false) {
    this.cancel();
    if (this.composing) return;
    const query = this.input.value.trim();
    if (!query) {
      this.results.replaceChildren();
      this.setState('idle');
      this.status.textContent = '';
      return;
    }
    // Keep the current layout during typing. Only show loading feedback when
    // the request outlasts the brief delay, and prevent stale result activation.
    this.setState('pending');
    const sequence = this.sequence;
    if (immediate) void this.search(query, sequence);
    else this.debounceTimer = setTimeout(() => this.search(query, sequence), 180);
  }

  async search(query, sequence) {
    const current = () => this.isConnected && sequence === this.sequence && this.dialog.hasAttribute('open');
    if (!current()) return;
    const controller = new AbortController();
    this.request = controller;
    this.loadingTimer = setTimeout(() => {
      if (current()) this.setState('loading');
    }, 140);
    let timedOut = false;
    this.timeoutTimer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 8000);
    try {
      let predictive = false;
      try {
        predictive = Boolean(JSON.parse(document.getElementById('shopify-features')?.textContent ?? '{}').predictiveSearch);
      } catch {}
      const url = new URL(predictive ? this.dataset.suggestUrl : this.dataset.searchUrl, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Invalid search origin');
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', 'nwp-predictive-search');
      if (predictive) {
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[limit_scope]', 'each');
      } else {
        url.searchParams.set('options[prefix]', 'last');
      }
      const key = url.href;
      const cached = this.cache.get(key);
      let html;
      if (cached && Date.now() - cached.time < 60000) html = cached.html;
      else {
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' } });
        if (!response.ok || (response.url && new URL(response.url).origin !== url.origin)) throw new Error('Search unavailable');
        html = await response.text();
      }
      if (!current()) return;
      const documentResult = new DOMParser().parseFromString(html, 'text/html');
      const result = documentResult.querySelector('[data-nwp-search-response]');
      if (!result) throw new Error('Missing search response');
      if (!cached || Date.now() - cached.time >= 60000) {
        this.cache.delete(key);
        this.cache.set(key, { html, time: Date.now() });
        if (this.cache.size > 12) this.cache.delete(this.cache.keys().next().value);
      }
      this.results.replaceChildren(document.importNode(result, true));
      this.resultsQuery = query;
      this.setState('results');
      this.querySelector('.nwp-search__body').scrollTop = 0;
      this.status.textContent = result.dataset.resultMessage;
    } catch (error) {
      if (current() && (timedOut || error.name !== 'AbortError')) this.setState('error');
    } finally {
      if (sequence === this.sequence) {
        clearTimeout(this.loadingTimer);
        clearTimeout(this.timeoutTimer);
        this.results.setAttribute('aria-busy', 'false');
        this.request = null;
      }
    }
  }

  navigate(event) {
    if (event.isComposing || this.composing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (['pending', 'loading'].includes(this.dataset.state)) return;
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const items = [...this.querySelectorAll('[data-search-item]')].filter(item => item.getClientRects().length && !item.closest('[hidden]'));
    if (!items.length) return;
    const onInput = event.target === this.input;
    const index = items.indexOf(event.target.closest('[data-search-item]'));
    if (!onInput && index < 0) return;
    if (onInput && ['Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    let destination;
    if (event.key === 'Home') destination = items[0];
    else if (event.key === 'End') destination = items.at(-1);
    else if (onInput) destination = event.key === 'ArrowDown' ? items[0] : items.at(-1);
    else destination = items[index + (event.key === 'ArrowDown' ? 1 : -1)] ?? this.input;
    destination.focus({ preventScroll: true });
    destination.scrollIntoView({ block: 'nearest', behavior: 'instant' });
  }
}

if (!customElements.get('nwp-search')) customElements.define('nwp-search', NwpSearch);
